import { type Node } from '@xyflow/react';

import { isSelectableForMonitoringSelectionMode } from '../../../../../../components/MonitoringAgent/utils/monitoringSelectionUtils';
import { AI_PANEL_SELECTABLE_UNIT_TYPES } from '../../../../../../constants/general';
import {
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_UNIT_TYPES,
} from '../../../../../../constants/interfaces';
import { getUnitIdFromNodeId } from '../../../../../../utils/AICreatorHelper';
import { getCountryDisplayName } from '../../../../../../utils/country';
import { getUnitDisplayName } from '../../../../../../utils/reactFlowHelpers';

import type IScenarioEntity from '../../../../../../components/AddScenarioSidebar/interfaces/IScenarioEntity';
import type { IAICardSelection } from '../../../../../../components/AICardPanel/interfaces/IAICardSelection';
import type { TMonitoringSelectionMode } from '../../../../../../constants/interfaces/IMonitoringAgentDefinition';
import type ISessionVCItem from '../../../../../../store/slices/valueChainMap/interfaces/ISessionVCItem';
import type { TCountryLookup } from '../../../../../../utils/country';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';

const buildUnitNodesById = (nodes: Node[]): Map<string, Node> => {
  const unitNodesById = new Map<string, Node>();

  nodes.forEach((nodeItem) => {
    if (nodeItem.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      unitNodesById.set(nodeItem.id, nodeItem);
    }
  });

  return unitNodesById;
};

export const selectAICardsFromUnitIds = (
  unitIds: number[],
  nodes: Node[],
): IAICardSelection[] => {
  const unitNodesById = buildUnitNodesById(nodes);

  return unitIds.reduce<IAICardSelection[]>((cards, unitId) => {
    const matchedNode = unitNodesById.get(`unit_${unitId}`);
    if (!matchedNode) {
      return cards;
    }

    const nodeData = matchedNode.data as unknown as ICustomUnitNodeData;
    const cardName = getUnitDisplayName(nodeData);
    const cardType = String(nodeData.type || '');
    const cardSwimlaneId = String(nodeData.swimlaneId || '');

    if (!cardName || !cardType || !cardSwimlaneId) {
      return cards;
    }

    cards.push({
      nodeId: matchedNode.id,
      name: cardName,
      type: cardType,
      swimlaneId: cardSwimlaneId,
    });

    return cards;
  }, []);
};

export const selectCardsFromSession = (
  sessionItem: ISessionVCItem | undefined,
  nodes: Node[],
  fallbackCardPools: IAICardSelection[][],
): IAICardSelection[] => {
  if (!sessionItem?.selectedUnits?.length) {
    return [];
  }

  const knownCardsByUnitId = new Map<number, IAICardSelection>();

  fallbackCardPools.forEach((cards) => {
    cards.forEach((card) => {
      const unitId = getUnitIdFromNodeId(card.nodeId);

      if (unitId !== null && !knownCardsByUnitId.has(unitId)) {
        knownCardsByUnitId.set(unitId, card);
      }
    });
  });

  const unitNodesById = buildUnitNodesById(nodes);

  return sessionItem.selectedUnits.reduce<IAICardSelection[]>((cards, { unitId }) => {
    const matchedNode = unitNodesById.get(`unit_${unitId}`);

    if (!matchedNode) {
      const fallbackCard = knownCardsByUnitId.get(unitId);

      if (fallbackCard) {
        cards.push(fallbackCard);
      }

      return cards;
    }

    const nodeData = matchedNode.data as unknown as ICustomUnitNodeData;
    const cardName = getUnitDisplayName(nodeData);
    const cardType = String(nodeData.type || '');
    const cardSwimlaneId = String(nodeData.swimlaneId || '');

    if (!cardName || !cardType || !cardSwimlaneId) {
      const fallbackCard = knownCardsByUnitId.get(unitId);

      if (fallbackCard) {
        cards.push(fallbackCard);
      }

      return cards;
    }

    cards.push({
      nodeId: matchedNode.id,
      name: cardName,
      type: cardType,
      swimlaneId: cardSwimlaneId,
    });

    return cards;
  }, []);
};

export const buildAllAvailableAICards = (
  nodes: Node[],
  selectableUnitTypes: ReadonlySet<string> = AI_PANEL_SELECTABLE_UNIT_TYPES,
  monitoringSelectionMode?: TMonitoringSelectionMode,
): IAICardSelection[] => {
  const swimlaneNameById = new Map<string, string>();

  nodes.forEach((node) => {
    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE && typeof node.data?.name === 'string') {
      swimlaneNameById.set(node.id, node.data.name);
    }
  });

  return nodes.reduce<IAICardSelection[]>((acc, node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) return acc;

    const nodeData = node.data as unknown as ICustomUnitNodeData;
    const unitType = String(nodeData.type || '');
    const swimlaneId = String(nodeData.swimlaneId || '');
    const unitName = getUnitDisplayName(nodeData);

    const isSelectable = monitoringSelectionMode
      ? isSelectableForMonitoringSelectionMode(
        monitoringSelectionMode,
        unitType,
        swimlaneId,
        nodes,
      )
      : selectableUnitTypes.has(unitType);

    if (!isSelectable || !swimlaneId || !unitName) return acc;

    acc.push({
      nodeId: node.id,
      name: unitName,
      type: unitType,
      swimlaneId,
      swimlaneName: swimlaneNameById.get(swimlaneId),
    });

    return acc;
  }, []);
};

export const buildBoMManufacturerCards = (nodes: Node[]): IAICardSelection[] => (
  nodes.reduce<IAICardSelection[]>((cards, nodeItem) => {
    if (nodeItem.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return cards;
    }
    const nodeData = nodeItem.data as unknown as ICustomUnitNodeData;
    const unitType = String(nodeData.type || '');
    if (
      unitType !== VALUE_CHAIN_UNIT_TYPES.SUPPLIER
      && unitType !== VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET
    ) {
      return cards;
    }
    const unitName = getUnitDisplayName(nodeData);
    const swimlaneId = String(nodeData.swimlaneId || '');
    if (!unitName || !swimlaneId) {
      return cards;
    }

    cards.push({
      nodeId: nodeItem.id,
      name: unitName,
      type: unitType,
      swimlaneId,
    });

    return cards;
  }, [])
);

export const buildBoardEntities = (
  nodes: Node[],
  countryLookup: TCountryLookup,
): IScenarioEntity[] => (
  nodes.reduce<IScenarioEntity[]>((entities, nodeItem) => {
    if (nodeItem.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return entities;
    }
    const nodeData = nodeItem.data as unknown as ICustomUnitNodeData;
    const unitType = String(nodeData.type || '');
    const isRelevantType = unitType === VALUE_CHAIN_UNIT_TYPES.SUPPLIER
      || unitType === VALUE_CHAIN_UNIT_TYPES.PRODUCT
      || unitType === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT;
    if (!isRelevantType) {
      return entities;
    }
    const unitName = getUnitDisplayName(nodeData);
    if (!unitName) {
      return entities;
    }
    entities.push({
      id: nodeData.id,
      name: unitName,
      country: getCountryDisplayName(nodeData.records?.[0]?.addressCountry, countryLookup),
      type: unitType,
    });
    return entities;
  }, [])
);
